import React from 'react';

/**
 * Service Icon Utility
 * Handles rendering of service icons consistently across the app
 */

/**
 * Render service icon from backend data
 * @param {string|JSX.Element} icon - Icon path, emoji, or JSX element from backend
 * @param {string} serviceName - Service name for alt text
 * @param {string} className - CSS classes for the icon
 * @returns {JSX.Element} Icon component
 */
export const renderServiceIcon = (icon, serviceName = 'Service', className = 'w-[85px] h-[85px] object-contain') => {
  // If icon is already a React element, return it as is
  if (React.isValidElement(icon)) {
    return icon;
  }
  
  // If icon is a file path (starts with /), render as image
  if (icon && typeof icon === 'string' && icon.startsWith('/')) {
    return <img src={icon} alt={serviceName} className={className} />;
  }
  
  // If icon is an emoji or text, render as text
  if (icon && typeof icon === 'string') {
    return <span className={`${className} flex items-center justify-center text-2xl`}>{icon}</span>;
  }
  
  // Fallback to default plumbing icon
  return <img src="/plumping.png" alt={serviceName} className={className} />;
};

/**
 * Get service icon by service ID/slug
 * @param {string} serviceId - Service identifier
 * @param {string} className - CSS classes for the icon
 * @returns {JSX.Element} Icon component
 */
export const getServiceIconById = (serviceId, className = 'w-[85px] h-[85px] object-contain') => {
  const iconMap = {
    'plumbing': '🔧',
    'electrical': '⚡',
    'ac-repair': '❄️',
    'deep-cleaning': '🧽',
    'moving': '📦',
    'painting': '🎨',
    'pest-control': '🐛'
  };
  
  const emojiIcon = iconMap[serviceId] || '🔧';
  const serviceLabel = serviceId ? serviceId.charAt(0).toUpperCase() + serviceId.slice(1).replace(/-/g, ' ') : 'Service';
  
  return (
    <span
      className={`${className} flex items-center justify-center text-2xl`}
      role="img"
      aria-label={serviceLabel}
      title={serviceLabel}
    >
      {emojiIcon}
    </span>
  );
};

/**
 * Get service color by service ID/slug
 * @param {string} serviceId - Service identifier
 * @returns {string} Tailwind color classes
 */
export const getServiceColor = (serviceId) => {
  const colorMap = {
    'plumbing': 'from-blue-500 to-blue-700',
    'electrical': 'from-yellow-500 to-yellow-700',
    'ac-repair': 'from-cyan-500 to-cyan-700',
    'deep-cleaning': 'from-green-500 to-green-700',
    'moving': 'from-purple-500 to-purple-700',
    'painting': 'from-pink-500 to-pink-700',
    'pest-control': 'from-orange-500 to-orange-700'
  };
  
  return colorMap[serviceId] || 'from-gray-500 to-gray-700';
};

const serviceIcons = {
  renderServiceIcon,
  getServiceIconById,
  getServiceColor,
};

export default serviceIcons;
